import { useState, useEffect } from 'react';
import { Download, FileText, Loader2, CheckCircle, AlertCircle, ChevronDown, ChevronUp, Plus, Trash2, Star, ExternalLink } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { formsApi, Signature, TerminalDisclaimerFormType } from '@/services/formsApi';
import { useNotificationStore } from '@/stores';
import { cleanDocumentNumber } from '@/utils/documentNumbers';

interface FormDownloadPanelProps {
  applicationNumber: string;
  applicationData?: any;
  isTabView?: boolean;
}

type FormType = 'rce' | 'ids' | 'terminal_disclaimer' | 'notice_of_appeal' | 'poa_transmittal' | 'transmittal';

const FORM_OPTIONS: { value: FormType; label: string; description: string }[] = [
  { value: 'rce', label: 'RCE (Request for Continued Examination)', description: 'Form SB/30 - Request for Continued Examination' },
  { value: 'ids', label: 'IDS (Information Disclosure Statement)', description: 'Form SB/08 - Application data and signature only' },
  { value: 'terminal_disclaimer', label: 'Terminal Disclaimer', description: 'Various Terminal Disclaimer forms' },
  { value: 'notice_of_appeal', label: 'Notice of Appeal', description: 'Notice of Appeal to the Patent Trial and Appeal Board' },
  { value: 'poa_transmittal', label: 'POA Transmittal', description: 'Power of Attorney Transmittal form' },
  { value: 'transmittal', label: 'Transmittal Form', description: 'General Transmittal form for USPTO filings' },
];

const TD_FORM_OPTIONS: { value: TerminalDisclaimerFormType; label: string; description: string; refLabel: string; refType: 'application' | 'patent' }[] = [
  {
    value: 'pending_vs_pending',
    label: 'Pending App vs Pending Reference App',
    description: 'For pending application with double patenting over another pending application',
    refLabel: 'Reference Application Number',
    refType: 'application',
  },
  {
    value: 'pending_vs_prior_patent',
    label: 'Pending App vs Prior Patent',
    description: 'For pending application with double patenting over an issued patent',
    refLabel: 'Reference Patent Number',
    refType: 'patent',
  },
  {
    value: 'design_petition',
    label: 'Design App with Petition (37 CFR 1.137)',
    description: 'For design application with petition to revive',
    refLabel: 'Reference Number',
    refType: 'patent',
  },
  {
    value: 'patent_vs_application',
    label: 'Issued Patent vs Pending App',
    description: 'For issued patent with double patenting over a pending application',
    refLabel: 'Reference Application Number',
    refType: 'application',
  },
  {
    value: 'patent_vs_patent',
    label: 'Issued Patent vs Another Patent',
    description: 'For issued patent with double patenting over another issued patent',
    refLabel: 'Reference Patent Number',
    refType: 'patent',
  },
];

type GenerationStatus = 'idle' | 'generating' | 'complete' | 'error';

export function FormDownloadPanel({ applicationNumber, applicationData, isTabView = false }: FormDownloadPanelProps) {
  const [isExpanded, setIsExpanded] = useState(isTabView); // Always expanded in tab view
  const [selectedForm, setSelectedForm] = useState<FormType>('rce');
  const [selectedSignatureId, setSelectedSignatureId] = useState<string>('none');
  const [signatures, setSignatures] = useState<Signature[]>([]);
  const [defaultSignatureId, setDefaultSignatureId] = useState<string | undefined>(undefined);
  const [loadingSignatures, setLoadingSignatures] = useState(false);
  const [generationStatus, setGenerationStatus] = useState<GenerationStatus>('idle');
  const [statusMessage, setStatusMessage] = useState('');
  const [showSignatureManagement, setShowSignatureManagement] = useState(false);
  const [newSignature, setNewSignature] = useState({ name: '', registrationNumber: '', label: '' });
  const [savingSignature, setSavingSignature] = useState(false);
  // Terminal Disclaimer specific fields
  const [tdFormType, setTdFormType] = useState<TerminalDisclaimerFormType>('pending_vs_pending');
  const [referenceNo, setReferenceNo] = useState('');
  const [referenceFilingDate, setReferenceFilingDate] = useState('');
  // POA Transmittal specific fields
  const [customerNumber, setCustomerNumber] = useState('');
  // Transmittal Form specific fields
  const [totalPages, setTotalPages] = useState('');
  const { addNotification } = useNotificationStore();

  // Fetch signatures when panel expands or in tab view
  useEffect(() => {
    if ((isExpanded || isTabView) && signatures.length === 0) {
      fetchSignatures();
    }
  }, [isExpanded, isTabView]);

  const fetchSignatures = async () => {
    setLoadingSignatures(true);
    try {
      const result = await formsApi.getSignatures();
      setSignatures(result.signatures || []);
      setDefaultSignatureId(result.defaultSignatureId);
      // Auto-select default signature if available
      if (result.defaultSignatureId) {
        setSelectedSignatureId(result.defaultSignatureId);
      } else if (result.signatures && result.signatures.length > 0 && result.signatures[0].id) {
        setSelectedSignatureId(result.signatures[0].id);
      }
      // Otherwise keep 'none' as default
    } catch (error) {
      console.error('Failed to fetch signatures:', error);
    } finally {
      setLoadingSignatures(false);
    }
  };

  const handleAddSignature = async () => {
    if (!newSignature.name.trim() || !newSignature.registrationNumber.trim()) {
      addNotification({
        type: 'error',
        title: 'Invalid Signature',
        message: 'Please enter name, signature, and registration number.',
        duration: 3000,
      });
      return;
    }

    setSavingSignature(true);
    try {
      const signature: Signature = {
        id: `sig-${Date.now()}`,
        name: newSignature.name.trim(),
        registrationNumber: newSignature.registrationNumber.trim(),
        label: newSignature.label.trim() || undefined,
      };

      const updatedSignatures = [...signatures, signature];
      await formsApi.updateSignatures(updatedSignatures, defaultSignatureId);

      setSignatures(updatedSignatures);
      setNewSignature({ name: '', registrationNumber: '', label: '' });

      // Auto-select the new signature if it's the first one
      if (updatedSignatures.length === 1 && signature.id) {
        setSelectedSignatureId(signature.id);
      }

      addNotification({
        type: 'success',
        title: 'Signature Added',
        message: 'Signature has been added and saved.',
        duration: 3000,
      });
    } catch (error: any) {
      addNotification({
        type: 'error',
        title: 'Error',
        message: error.message || 'Failed to save signature',
        duration: 5000,
      });
    } finally {
      setSavingSignature(false);
    }
  };

  const handleRemoveSignature = async (signatureId: string) => {
    setSavingSignature(true);
    try {
      const updatedSignatures = signatures.filter(sig => sig.id !== signatureId);
      const newDefaultId = defaultSignatureId === signatureId ? undefined : defaultSignatureId;

      await formsApi.updateSignatures(updatedSignatures, newDefaultId);

      setSignatures(updatedSignatures);
      setDefaultSignatureId(newDefaultId);

      // Update selected signature if it was removed
      if (selectedSignatureId === signatureId) {
        setSelectedSignatureId(updatedSignatures.length > 0 && updatedSignatures[0].id ? updatedSignatures[0].id : 'none');
      }

      addNotification({
        type: 'success',
        title: 'Signature Removed',
        message: 'Signature has been removed.',
        duration: 3000,
      });
    } catch (error: any) {
      addNotification({
        type: 'error',
        title: 'Error',
        message: error.message || 'Failed to remove signature',
        duration: 5000,
      });
    } finally {
      setSavingSignature(false);
    }
  };

  const handleSetDefaultSignature = async (signatureId: string) => {
    setSavingSignature(true);
    try {
      await formsApi.updateSignatures(signatures, signatureId);
      setDefaultSignatureId(signatureId);

      addNotification({
        type: 'success',
        title: 'Default Signature Set',
        message: 'Default signature has been updated.',
        duration: 3000,
      });
    } catch (error: any) {
      addNotification({
        type: 'error',
        title: 'Error',
        message: error.message || 'Failed to set default signature',
        duration: 5000,
      });
    } finally {
      setSavingSignature(false);
    }
  };

  const handleGenerateForm = async () => {
    if (!applicationNumber) {
      addNotification({
        type: 'error',
        title: 'Error',
        message: 'No application selected',
        duration: 3000,
      });
      return;
    }

    setGenerationStatus('generating');
    setStatusMessage('Submitting form generation request...');

    try {
      // Get selected signature (undefined if "none" is selected)
      const selectedSignature = selectedSignatureId && selectedSignatureId !== 'none'
        ? signatures.find(s => s.id === selectedSignatureId || `sig-${s.name}` === selectedSignatureId)
        : undefined;

      // Submit form generation request based on form type
      let jobId: string;
      if (selectedForm === 'ids') {
        jobId = await formsApi.submitIdsForm({
          appNo: cleanDocumentNumber(applicationNumber, 'application'),
          signature: selectedSignature,
        });
      } else if (selectedForm === 'terminal_disclaimer') {
        jobId = await formsApi.submitTerminalDisclaimerForm({
          appNo: cleanDocumentNumber(applicationNumber, 'application'),
          tdFormType: tdFormType,
          referenceNo: referenceNo || undefined,
          referenceFilingDate: referenceFilingDate || undefined,
          signature: selectedSignature,
        });
      } else if (selectedForm === 'notice_of_appeal') {
        jobId = await formsApi.submitNoticeOfAppealForm({
          appNo: cleanDocumentNumber(applicationNumber, 'application'),
          signature: selectedSignature,
        });
      } else if (selectedForm === 'poa_transmittal') {
        jobId = await formsApi.submitPoaTransmittalForm({
          appNo: cleanDocumentNumber(applicationNumber, 'application'),
          signature: selectedSignature,
          customerNumber: customerNumber || undefined,
        });
      } else if (selectedForm === 'transmittal') {
        jobId = await formsApi.submitTransmittalForm({
          appNo: cleanDocumentNumber(applicationNumber, 'application'),
          signature: selectedSignature,
          totalPages: totalPages || undefined,
        });
      } else {
        jobId = await formsApi.submitRceForm({
          appNo: cleanDocumentNumber(applicationNumber, 'application'),
          signature: selectedSignature,
        });
      }

      setStatusMessage('Generating PDF...');

      // Wait for job completion
      const result = await formsApi.waitForJobCompletion(jobId, (status) => {
        if (status.status === 'processing') {
          setStatusMessage('Processing form data...');
        }
      });

      if (result.status === 'complete' && result.fileKey) {
        setStatusMessage('Downloading file...');

        // Download the file
        const blob = await formsApi.downloadFile(result.fileKey);

        // Create download link with appropriate filename
        const formPrefixMap: Record<FormType, string> = {
          'rce': 'RCE',
          'ids': 'IDS',
          'terminal_disclaimer': 'Terminal_Disclaimer',
          'notice_of_appeal': 'Notice_of_Appeal',
          'poa_transmittal': 'POA_Transmittal',
          'transmittal': 'Transmittal'
        };
        const formPrefix = formPrefixMap[selectedForm];
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${formPrefix}_Form_${applicationNumber}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);

        setGenerationStatus('complete');
        setStatusMessage('Form downloaded successfully!');

        const formNameMap: Record<FormType, string> = {
          'rce': 'RCE',
          'ids': 'IDS',
          'terminal_disclaimer': 'Terminal Disclaimer',
          'notice_of_appeal': 'Notice of Appeal',
          'poa_transmittal': 'POA Transmittal',
          'transmittal': 'Transmittal'
        };
        addNotification({
          type: 'success',
          title: 'Form Downloaded',
          message: `${formNameMap[selectedForm]} form for application ${applicationNumber} has been downloaded.`,
          duration: 5000,
        });

        // Reset status after a delay
        setTimeout(() => {
          setGenerationStatus('idle');
          setStatusMessage('');
        }, 3000);
      }
    } catch (error: any) {
      console.error('Form generation failed:', error);
      setGenerationStatus('error');
      setStatusMessage(error.message || 'Form generation failed');

      addNotification({
        type: 'error',
        title: 'Form Generation Failed',
        message: error.message || 'Failed to generate form. Please try again.',
        duration: 5000,
      });

      // Reset status after a delay
      setTimeout(() => {
        setGenerationStatus('idle');
        setStatusMessage('');
      }, 5000);
    }
  };

  const getSelectedSignatureLabel = () => {
    if (!selectedSignatureId || selectedSignatureId === 'none') {
      return 'No signature';
    }
    const sig = signatures.find(s => s.id === selectedSignatureId || `sig-${s.name}` === selectedSignatureId);
    return sig ? (sig.label || sig.name) : 'Select signature';
  };

  // Content to render (shared between tab view and card view)
  const formContent = (
    <div className="space-y-6">
      {/* Form Type Selection */}
      <div className="space-y-2">
        <Label htmlFor="form-type">Form Type</Label>
        <Select value={selectedForm} onValueChange={(value: FormType) => setSelectedForm(value)}>
          <SelectTrigger id="form-type">
            <SelectValue placeholder="Select form type" />
          </SelectTrigger>
          <SelectContent>
            {FORM_OPTIONS.map((form) => (
              <SelectItem key={form.value} value={form.value}>
                <div>
                  <div className="font-medium">{form.label}</div>
                  <div className="text-xs text-muted-foreground">{form.description}</div>
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Terminal Disclaimer Options */}
      {selectedForm === 'terminal_disclaimer' && (
        <div className="space-y-4 p-3 border rounded-lg bg-gray-50">
          {/* TD Form Type Selection */}
          <div className="space-y-2">
            <Label htmlFor="td-form-type" className="text-sm font-medium text-gray-700">Terminal Disclaimer Type</Label>
            <Select value={tdFormType} onValueChange={(value: TerminalDisclaimerFormType) => setTdFormType(value)}>
              <SelectTrigger id="td-form-type" className="bg-white">
                <SelectValue placeholder="Select form type" />
              </SelectTrigger>
              <SelectContent>
                {TD_FORM_OPTIONS.map((form) => (
                  <SelectItem key={form.value} value={form.value}>
                    <div>
                      <div className="font-medium text-sm">{form.label}</div>
                      <div className="text-xs text-muted-foreground">{form.description}</div>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Reference Fields */}
          <div className="space-y-2">
            <h5 className="text-sm font-medium text-gray-700">Reference Information (Optional)</h5>
            <p className="text-xs text-muted-foreground">
              The {TD_FORM_OPTIONS.find(f => f.value === tdFormType)?.refType === 'patent' ? 'patent' : 'application'} causing the double patenting rejection. Leave blank to fill manually.
            </p>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label htmlFor="ref-no" className="text-xs">
                  {TD_FORM_OPTIONS.find(f => f.value === tdFormType)?.refLabel || 'Reference Number'}
                </Label>
                <Input
                  id="ref-no"
                  placeholder={TD_FORM_OPTIONS.find(f => f.value === tdFormType)?.refType === 'patent' ? 'e.g., 10123456' : 'e.g., 16123456'}
                  value={referenceNo}
                  onChange={(e) => setReferenceNo(e.target.value)}
                  className="h-8 text-sm"
                />
              </div>
              {/* Filing date only for application references */}
              {(tdFormType === 'pending_vs_pending' || tdFormType === 'patent_vs_application') && (
                <div className="space-y-1">
                  <Label htmlFor="ref-filing-date" className="text-xs">Filing Date</Label>
                  <Input
                    id="ref-filing-date"
                    placeholder="e.g., 01/15/2023"
                    value={referenceFilingDate}
                    onChange={(e) => setReferenceFilingDate(e.target.value)}
                    className="h-8 text-sm"
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* POA Transmittal Options */}
      {selectedForm === 'poa_transmittal' && (
        <div className="space-y-3 p-3 border rounded-lg bg-gray-50">
          <div className="space-y-1">
            <Label htmlFor="customer-number" className="text-sm font-medium text-gray-700">Customer Number (Optional)</Label>
            <p className="text-xs text-muted-foreground">
              USPTO customer number for correspondence address.
            </p>
            <Input
              id="customer-number"
              placeholder="e.g., 12345"
              value={customerNumber}
              onChange={(e) => setCustomerNumber(e.target.value)}
              className="h-8 text-sm max-w-xs"
            />
          </div>
        </div>
      )}

      {/* Transmittal Form Options */}
      {selectedForm === 'transmittal' && (
        <div className="space-y-3 p-3 border rounded-lg bg-gray-50">
          <div className="space-y-1">
            <Label htmlFor="total-pages" className="text-sm font-medium text-gray-700">Total Pages (Optional)</Label>
            <p className="text-xs text-muted-foreground">
              Total number of pages being submitted.
            </p>
            <Input
              id="total-pages"
              placeholder="e.g., 25"
              value={totalPages}
              onChange={(e) => setTotalPages(e.target.value)}
              className="h-8 text-sm max-w-xs"
            />
          </div>
        </div>
      )}

      {/* Signature Selection */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Label htmlFor="signature">Signature (Optional)</Label>
            <a
              href="https://www.uspto.gov/sites/default/files/documents/sigexamples_alt_text.pdf"
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1"
              title="USPTO Signature Examples"
            >
              Examples <ExternalLink className="w-3 h-3" />
            </a>
          </div>
          <Button
            variant="ghost"
            size="sm"
            className="h-7 text-xs"
            onClick={() => setShowSignatureManagement(!showSignatureManagement)}
          >
            {showSignatureManagement ? 'Hide' : 'Manage'} Signatures
            {showSignatureManagement ? <ChevronUp className="w-3 h-3 ml-1" /> : <ChevronDown className="w-3 h-3 ml-1" />}
          </Button>
        </div>
        {loadingSignatures ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="w-4 h-4 animate-spin" />
            Loading signatures...
          </div>
        ) : signatures.length > 0 ? (
          <Select value={selectedSignatureId} onValueChange={setSelectedSignatureId}>
            <SelectTrigger id="signature">
              <SelectValue placeholder="Select signature">{getSelectedSignatureLabel()}</SelectValue>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">No signature</SelectItem>
              {signatures.map((sig) => (
                <SelectItem key={sig.id} value={sig.id || `sig-${sig.name}`}>
                  {sig.label || sig.name} ({sig.registrationNumber})
                  {defaultSignatureId === sig.id && ' ★'}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : (
          <p className="text-sm text-muted-foreground">
            No signatures configured. Click "Manage Signatures" to add one.
          </p>
        )}
      </div>

      {/* Signature Management Section */}
      {showSignatureManagement && (
        <div className="border rounded-lg p-4 space-y-4 bg-gray-50">
          <h4 className="font-medium text-sm">Manage Signatures</h4>

          {/* Add New Signature Form */}
          <div className="space-y-3 p-3 border rounded bg-white">
            <h5 className="text-sm font-medium text-gray-700">Add New Signature</h5>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label htmlFor="sig-name" className="text-xs">Name *</Label>
                <Input
                  id="sig-name"
                  placeholder="John Smith"
                  value={newSignature.name}
                  onChange={(e) => setNewSignature(prev => ({ ...prev, name: e.target.value }))}
                  className="h-8 text-sm"
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="sig-reg" className="text-xs">Registration # *</Label>
                <Input
                  id="sig-reg"
                  placeholder="12345"
                  value={newSignature.registrationNumber}
                  onChange={(e) => setNewSignature(prev => ({ ...prev, registrationNumber: e.target.value }))}
                  className="h-8 text-sm"
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="sig-label" className="text-xs">Label (Optional)</Label>
                <Input
                  id="sig-label"
                  placeholder="Primary Attorney"
                  value={newSignature.label}
                  onChange={(e) => setNewSignature(prev => ({ ...prev, label: e.target.value }))}
                  className="h-8 text-sm"
                />
              </div>
            </div>
            <Button
              onClick={handleAddSignature}
              size="sm"
              variant="outline"
              disabled={savingSignature}
              className="h-8"
            >
              {savingSignature ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Plus className="w-3 h-3 mr-1" />}
              Add Signature
            </Button>
          </div>

          {/* Saved Signatures List */}
          {signatures.length > 0 && (
            <div className="space-y-2">
              <h5 className="text-sm font-medium text-gray-700">Saved Signatures</h5>
              <div className="space-y-2">
                {signatures.map((sig) => (
                  <div key={sig.id} className="flex items-center justify-between p-2 border rounded bg-white">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-sm truncate">{sig.label || sig.name}</span>
                        {defaultSignatureId === sig.id && (
                          <span className="text-xs bg-yellow-100 text-yellow-800 px-1.5 py-0.5 rounded">Default</span>
                        )}
                      </div>
                      <div className="text-xs text-gray-500">Reg# {sig.registrationNumber}</div>
                    </div>
                    <div className="flex items-center gap-1 ml-2">
                      {defaultSignatureId !== sig.id && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 w-7 p-0"
                          onClick={() => handleSetDefaultSignature(sig.id!)}
                          disabled={savingSignature}
                          title="Set as default"
                        >
                          <Star className="w-3.5 h-3.5" />
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 w-7 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                        onClick={() => handleRemoveSignature(sig.id!)}
                        disabled={savingSignature}
                        title="Remove signature"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <p className="text-xs text-muted-foreground">
            Signatures are shared with the IDS feature.
          </p>
        </div>
      )}

      {/* Status Message */}
      {statusMessage && (
        <div className={`flex items-center gap-2 text-sm ${
          generationStatus === 'error' ? 'text-red-600' :
          generationStatus === 'complete' ? 'text-green-600' :
          'text-blue-600'
        }`}>
          {generationStatus === 'generating' && <Loader2 className="w-4 h-4 animate-spin" />}
          {generationStatus === 'complete' && <CheckCircle className="w-4 h-4" />}
          {generationStatus === 'error' && <AlertCircle className="w-4 h-4" />}
          {statusMessage}
        </div>
      )}

      {/* Generate Button */}
      <Button
        onClick={handleGenerateForm}
        disabled={generationStatus === 'generating'}
        className="w-full max-w-xs"
      >
        {generationStatus === 'generating' ? (
          <>
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            Generating...
          </>
        ) : (
          <>
            <Download className="w-4 h-4 mr-2" />
            Generate & Download Form
          </>
        )}
      </Button>

      {/* Info text */}
      <p className="text-xs text-muted-foreground">
        The form will be pre-filled with application information from USPTO.
        You may need to complete additional fields manually.
      </p>
    </div>
  );

  // Tab view: render content directly in a card
  if (isTabView) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <FileText className="w-5 h-5" />
            Download USPTO Forms
          </CardTitle>
        </CardHeader>
        <CardContent>
          {formContent}
        </CardContent>
      </Card>
    );
  }

  // Card view: collapsible panel
  return (
    <Card className="mt-4">
      <CardHeader
        className="cursor-pointer py-3"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2">
            <FileText className="w-5 h-5" />
            Download USPTO Forms
          </CardTitle>
          <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
            {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </Button>
        </div>
      </CardHeader>

      {isExpanded && (
        <CardContent className="pt-0">
          {formContent}
        </CardContent>
      )}
    </Card>
  );
}
